import React, { useState } from "react";
import { useI18n } from "../../i18n";
import { categoryIcon } from "../utils/categories";
import { productImageUrl } from "../utils/images";
import { formatAmount, round2 } from "../utils/money";
import { bestOfferFor } from "../utils/offers";
import { lowStockLimit } from "../utils/groups";
import { isPackItem } from "../utils/packs";

const StockPill = ({ stock, lowStockThreshold }) => {
  const { t } = useI18n();

  if (stock <= 0) {
    return (
      <span className="px-1.5 py-0.5 rounded-md bg-red-500/90 text-white text-[10px] font-bold uppercase tracking-wide">
        {t("pos.tile.out")}
      </span>
    );
  }

  if (stock <= lowStockLimit(lowStockThreshold)) {
    return (
      <span className="px-1.5 py-0.5 rounded-md bg-amber-500/90 text-slate-900 text-[10px] font-bold tabular-nums">
        {t("pos.tile.left", { count: stock })}
      </span>
    );
  }

  return null;
};

/**
 * One tap adds the product to the ticket; tapping again adds another unit.
 * The whole tile is the target so it stays reachable with a thumb.
 */
const ProductTile = ({ product, quantity, lowStockThreshold, onAdd }) => {
  const { t } = useI18n();
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc = imageFailed ? null : productImageUrl(product.image);
  const isOut = product.stock_quantity <= 0;
  const inTicket = quantity > 0;

  // What one unit actually rings up at, so the tile agrees with the total
  // rather than the cashier finding the offer at payment. Worked out by the
  // same helper as the ticket, which mirrors the server.
  const unitOffer = bestOfferFor(product, 1);
  const price = round2(
    Number(product.unit_price) - (unitOffer ? unitOffer.amount : 0),
  );
  const shelfPrice = Number(product.price);
  const reduced = price < shelfPrice;
  // A pack has no brand; what the cashier needs is what goes in the bag
  const subtitle = isPackItem(product)
    ? t("pos.tile.packOf", { count: product.components.length })
    : product.brand;

  // "Buy 10, get 2 free" never lowers a single unit, so it is described
  // instead — the cashier still needs to be able to mention it
  const quantityOffer = unitOffer
    ? null
    : (product.offer?.all || [product.offer]).find(
        (offer) => offer?.offer_type === "buy_x_get_y",
      );

  return (
    <button
      type="button"
      onClick={() => onAdd(product)}
      aria-label={t("pos.tile.add", {
        name: product.name,
        price: formatAmount(price),
      })}
      className={`group relative flex flex-col text-start rounded-2xl overflow-hidden border transition-all active:scale-[0.97] ${
        inTicket
          ? "border-emerald-400 bg-emerald-500/10 shadow-lg shadow-emerald-500/10"
          : "border-slate-800 bg-slate-900 hover:border-slate-600 hover:bg-slate-800"
      } ${isOut ? "opacity-60" : ""}`}
    >
      <div className="relative h-20 sm:h-24 bg-slate-800/60 flex items-center justify-center overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-3xl">{categoryIcon(product.category)}</span>
        )}

        {/* Stacked rather than both pinned to the same corner, where the
            offer badge used to hide an "Out" pill underneath it */}
        <div className="absolute top-1.5 start-1.5 flex flex-col items-start gap-1">
          {/* A live promotion, so the cashier can tell the customer about it
              rather than it appearing unexplained in the total */}
          {product.offer && (
            <span
              title={(unitOffer || product.offer).title}
              className="px-1.5 py-0.5 rounded-md bg-amber-500 text-slate-900 text-[10px] font-bold"
            >
              {t("pos.tile.offer")}
            </span>
          )}
          <StockPill
            stock={product.stock_quantity}
            lowStockThreshold={lowStockThreshold}
          />
        </div>

        {product.discount_percentage > 0 && (
          <span className="absolute top-1.5 end-1.5 px-1.5 py-0.5 rounded-md bg-primary-500 text-white text-[10px] font-bold">
            -{Math.round(product.discount_percentage)}%
          </span>
        )}

        {inTicket && (
          <span className="absolute bottom-1.5 end-1.5 min-w-6 h-6 px-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center tabular-nums">
            {quantity}
          </span>
        )}
      </div>

      <div className="flex-1 flex flex-col p-2.5">
        <p className="text-[13px] leading-tight font-semibold text-white line-clamp-2">
          {product.name}
        </p>
        {subtitle && (
          <p className="text-[11px] text-slate-400 truncate mt-0.5">
            {subtitle}
          </p>
        )}
        <div className="flex flex-wrap items-baseline gap-x-1.5 mt-auto pt-1.5">
          <span className="text-base font-bold text-emerald-400 tabular-nums">
            {formatAmount(price)}
          </span>
          {reduced && (
            <span className="text-[11px] text-slate-500 line-through tabular-nums">
              {formatAmount(shelfPrice)}
            </span>
          )}
        </div>
        {quantityOffer && (
          <p className="text-[10px] font-semibold text-amber-400 truncate">
            {t("pos.tile.buyGet", {
              buy: quantityOffer.buy_quantity,
              get: quantityOffer.get_quantity,
            })}
          </p>
        )}
      </div>
    </button>
  );
};

export default ProductTile;
